import { useState, useCallback, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { useProject } from '../lib/project-context';
import { useQuery } from '@tanstack/react-query';
import { institutionAPI } from '../lib/api';
import {
  LayoutDashboard, FolderKanban, LineChart, TrendingUp, HardHat, BarChart3,
  FileBarChart, Bot, HelpCircle, Settings, ChevronRight,
  LogOut, User, Building2, Lock, PanelLeftClose, PanelLeft, ShieldCheck
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from './ui/dropdown-menu';
import { Toaster } from './ui/sonner';
import { ROLE_MAP } from '../lib/utils';
import { GuidedTour, ActiveContextBar } from './GuidedExperience';

/* ════════════════════════════════════════════
   Module Detection
   ════════════════════════════════════════════ */
function useActiveModule() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/analyzer') || pathname.startsWith('/projects/')) return 'financial';
  if (pathname.startsWith('/market')) return 'market';
  if (pathname.startsWith('/construction')) return 'construction';
  if (pathname.startsWith('/reports')) return 'financial';
  if (pathname.startsWith('/capital-stack')) return 'financial';
  if (pathname.startsWith('/copilot')) return 'financial';
  return 'financial';
}

/* ════════════════════════════════════════════
   Navigation Configuration
   ════════════════════════════════════════════ */
const NAV_SECTIONS = [
  {
    label: null,
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Dashboard', key: 'dashboard' },
      { to: '/projects', icon: FolderKanban, label: 'Proyectos', key: 'projects' },
    ],
  },
  {
    label: 'Módulos',
    items: [
      { to: '/analyzer', icon: LineChart, label: 'Financial Intelligence', key: 'analyzer', requiresProject: true, module: 'financial' },
      { to: '/market', icon: TrendingUp, label: 'Market Intelligence', key: 'market', requiresProject: true, module: 'market' },
      { to: '/construction', icon: HardHat, label: 'Construction Intelligence', key: 'construction', requiresProject: true, module: 'construction' },
    ],
  },
  {
    label: 'Herramientas',
    items: [
      { to: '/reports', icon: FileBarChart, label: 'Reportes', key: 'reports', requiresProject: true },
      { to: '/copilot', icon: Bot, label: 'Copiloto IA', key: 'copilot', requiresProject: true },
    ],
  },
];

const BOTTOM_ITEMS_BASE = [
  { to: '/help', icon: HelpCircle, label: 'Centro de Ayuda', key: 'help' },
  { to: '/settings', icon: Settings, label: 'Configuración', key: 'settings' },
];

/* ════════════════════════════════════════════
   Breadcrumbs
   ════════════════════════════════════════════ */
/* ════════════════════════════════════════════
   Sidebar Nav Item
   ════════════════════════════════════════════ */
function SidebarItem({ item, collapsed, hasProject }) {
  const disabled = (item.requiresProject && !hasProject) || item.comingSoon;
  // Only module items get colored active state; everything else is neutral
  const isModule = !!item.module;
  const moduleColor = item.module === 'market' ? 'var(--market-primary)' : item.module === 'construction' ? 'var(--construction-primary)' : 'var(--financial-primary)';

  if (disabled) {
    return (
      <div
        className="group flex items-center gap-3 rounded-lg px-3 py-2 text-sm cursor-not-allowed select-none"
        style={{ color: 'var(--sidebar-muted)' }}
        data-testid={`sidebar-nav-${item.key}`}
        title={item.comingSoon ? 'Próximamente' : 'Selecciona un proyecto'}
      >
        <item.icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span className="truncate flex-1">{item.label}</span>}
        {!collapsed && item.comingSoon && (
          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-medium" style={{ background: 'var(--sidebar-hover)', color: 'var(--sidebar-muted)' }}>Soon</span>
        )}
        {!collapsed && item.requiresProject && !hasProject && !item.comingSoon && (
          <Lock className="h-3 w-3" style={{ color: 'var(--sidebar-muted)' }} />
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      data-testid={`sidebar-nav-${item.key}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all duration-150 ${
          isActive ? 'font-medium' : ''
        }`
      }
      style={({ isActive }) =>
        isActive
          ? isModule
            ? { background: `color-mix(in srgb, ${moduleColor} 10%, transparent)`, color: moduleColor }
            : { background: 'var(--sidebar-hover)', color: 'var(--sidebar-fg)' }
          : { color: 'var(--text-secondary)' }
      }
    >
      <item.icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}

/* ════════════════════════════════════════════
   Context Indicators (read-only)
   ════════════════════════════════════════════ */
function ContextIndicator() {
  const { activeProject, activeScenario } = useProject();
  const navigate = useNavigate();

  if (!activeProject) return null;

  return (
    <div className="flex items-center gap-1.5" data-testid="context-indicator">
      <FolderKanban className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--accent-primary)' }} />
      <button
        onClick={() => navigate(`/projects/${activeProject.id}`)}
        className="text-xs font-semibold truncate max-w-[180px] hover:underline transition-colors"
        style={{ color: 'var(--text-primary)' }}
        data-testid="breadcrumb-project-link"
      >
        {activeProject.name}
      </button>
      {activeScenario && (
        <>
          <ChevronRight className="h-3 w-3 shrink-0" style={{ color: 'var(--border-default)' }} />
          <span className="text-xs truncate max-w-[260px]" style={{ color: 'var(--text-secondary)' }} data-testid="breadcrumb-scenario-label">
            {activeScenario.name}
          </span>
        </>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════
   Main Layout
   ════════════════════════════════════════════ */
export function AppLayout({ children }) {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar-collapsed') === 'true');
  const { user, logout } = useAuth();
  const { activeProject } = useProject();
  const navigate = useNavigate();
  const activeModule = useActiveModule();

  // Apply module theme to root
  useEffect(() => {
    document.documentElement.setAttribute('data-module', activeModule);
  }, [activeModule]);

  const toggleSidebar = useCallback(() => {
    setCollapsed(prev => { localStorage.setItem('sidebar-collapsed', !prev); return !prev; });
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const hasProject = !!activeProject;
  const sidebarW = collapsed ? 64 : 240;

  // Portfolio visible only for multi-company institutions
  const { data: instData } = useQuery({
    queryKey: ['institutionCompanyCount'],
    queryFn: () => institutionAPI.dashboard().then(r => r.data),
    enabled: !!user?.institution_id,
    staleTime: 60 * 1000,
  });
  const showPortfolio = !!user?.institution_id && (instData?.companies_count || 0) > 1;
  const isSuperAdmin = user?.role === 'super_admin';

  // Build nav sections dynamically. Super admin only sees the platform admin
  // panel (no operational modules) — they're not tenant-scoped.
  const navSections = isSuperAdmin
    ? [
        {
          label: 'Plataforma',
          items: [
            { to: '/admin', icon: ShieldCheck, label: 'Admin Plataforma', key: 'admin' },
          ],
        },
      ]
    : [
        {
          label: null,
          items: [
            ...(showPortfolio ? [{ to: '/institutional', icon: Building2, label: 'Portfolio', key: 'institutional' }] : []),
            { to: '/', icon: LayoutDashboard, label: 'Dashboard', key: 'dashboard' },
            { to: '/projects', icon: FolderKanban, label: 'Proyectos', key: 'projects' },
          ],
        },
        ...NAV_SECTIONS.slice(1),
      ];

  // Bottom items: super admin sees only Settings (for password/profile),
  // tenant users see help + settings.
  const bottomItems = isSuperAdmin
    ? [{ to: '/settings', icon: Settings, label: 'Configuración', key: 'settings' }]
    : BOTTOM_ITEMS_BASE;

  const { isImpersonating, stopImpersonation } = useAuth();
  const impersonating = isImpersonating();

  return (
    <div className="flex flex-col h-screen overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
      {impersonating && (
        <div className="w-full flex items-center justify-between px-4 py-2 text-white text-xs font-medium" style={{ background: '#D92D20' }} data-testid="impersonation-banner">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Modo soporte técnico — estás viendo la plataforma como <strong>{user?.full_name} ({user?.email})</strong> · rol <em>{user?.role}</em>. Todas tus acciones se registran en auditoría.</span>
          </div>
          <button
            className="px-3 py-1 rounded-md bg-white/20 hover:bg-white/30 transition-colors text-white text-[11px] font-semibold"
            onClick={() => { stopImpersonation(); window.location.href = '/admin'; }}
            data-testid="stop-impersonation-btn"
          >
            Salir de impersonación
          </button>
        </div>
      )}
      <div className="flex flex-1 overflow-hidden">
      {/* ── Sidebar ── */}
      <aside
        data-testid="app-sidebar"
        className="flex flex-col h-full shrink-0 transition-[width] duration-200 ease-out"
        style={{ width: sidebarW, background: 'var(--sidebar-bg)', borderRight: '1px solid var(--sidebar-border)' }}
      >
        {/* Logo — smooth crossfade between full logo and K icon (oficial SVG v2) */}
        <div className="flex items-center justify-center h-16 shrink-0 relative">
          <img
            src="/assets/stratika-logo.svg"
            alt="Stratika"
            className="h-9 absolute transition-all duration-300 ease-in-out"
            style={{ objectFit: 'contain', opacity: collapsed ? 0 : 1, transform: collapsed ? 'scale(0.6)' : 'scale(1)' }}
          />
          <img
            src="/assets/stratika-isotipo.svg"
            alt="S"
            className="h-8 absolute transition-all duration-300 ease-in-out"
            style={{ objectFit: 'contain', opacity: collapsed ? 1 : 0, transform: collapsed ? 'scale(1)' : 'scale(1.4)' }}
          />
        </div>

        <div className="mx-3 h-px" style={{ background: 'var(--sidebar-border)' }} />

        {/* Navigation */}
        <nav className="flex-1 px-2 py-3 overflow-y-auto space-y-4">
          {navSections.map((section, si) => (
            <div key={si}>
              {section.label && !collapsed && (
                <div className="px-3 mb-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: 'var(--sidebar-muted)' }}>{section.label}</span>
                </div>
              )}
              {section.label && collapsed && <div className="mx-3 h-px my-1" style={{ background: 'var(--sidebar-border)' }} />}
              <div className="space-y-0.5">
                {section.items.map(item => (
                  <SidebarItem key={item.key} item={item} collapsed={collapsed} hasProject={hasProject} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Bottom */}
        <div className="px-2 pb-2 space-y-0.5">
          {bottomItems.map(item => (
            <SidebarItem key={item.key} item={item} collapsed={collapsed} hasProject={hasProject} />
          ))}

          <div className="mx-3 h-px !my-2" style={{ background: 'var(--sidebar-border)' }} />

          {/* User */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 w-full rounded-lg px-3 py-2 text-sm transition-all duration-150 hover:bg-[var(--sidebar-hover)]" data-testid="user-menu">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt={user.full_name} className="w-7 h-7 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold text-white shrink-0" style={{ background: 'var(--accent-primary)' }}>
                    {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                )}
                {!collapsed && (
                  <div className="text-left overflow-hidden flex-1">
                    <div className="text-xs font-medium truncate" style={{ color: 'var(--sidebar-fg)' }}>{user?.full_name}</div>
                    <div className="text-[10px]" style={{ color: 'var(--sidebar-muted)' }}>{ROLE_MAP[user?.role] || user?.role}</div>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{user?.full_name}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')}><User className="mr-2 h-4 w-4" /> Mi Perfil</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')}><Settings className="mr-2 h-4 w-4" /> Configuración</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Collapse + Powered by */}
        <div>
          {!collapsed && (
          <div className="pt-1 pb-2 flex items-center justify-center border-t" style={{ borderColor: 'var(--sidebar-border)' }}>
            <a href="https://www.swaraya.ai" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 py-2 opacity-80 hover:opacity-100 transition-opacity">
              <span className="text-[9px] uppercase tracking-[0.14em]" style={{ color: 'var(--sidebar-muted)' }}>Powered by</span>
              <img src="/swaraya-wordmark.png" alt="Swaraya" className="h-3.5" />
            </a>
          </div>
          )}
          <button
            onClick={toggleSidebar}
            className="h-8 w-full flex items-center justify-center transition-colors duration-150 hover:bg-[var(--sidebar-hover)]"
            style={{ borderTop: '1px solid var(--sidebar-border)', color: 'var(--sidebar-muted)' }}
            data-testid="sidebar-toggle-button"
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>
      </aside>

      {/* ── Main Area ── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar — clean and informative */}
        <header className="h-12 shrink-0 flex items-center justify-between px-5" style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-default)' }} data-testid="topbar">
          <div className="flex items-center gap-3 min-w-0">
            {user?.company_name && (
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{user.company_name}</span>
            )}
            <ContextIndicator />
          </div>
          <div />
        </header>

        <main className="flex-1 overflow-y-auto" data-testid="main-content">
          {children}
        </main>
      </div>
      </div>

      <Toaster position="top-right" toastOptions={{ style: { padding: '6px 12px', fontSize: '12px', minWidth: 'auto', width: 'fit-content' } }} offset={56} />
      <GuidedTour />
    </div>
  );
}
